/* eslint-disable import/order */
import { GetTokenResult } from '@capacitor-firebase/messaging'
import { Capacitor } from '@capacitor/core'
import { Device, DeviceId, DeviceInfo } from '@capacitor/device'
import { useIonModal } from '@ionic/react'
import { OverlayEventDetail } from '@ionic/react/dist/types/components/react-component-lib/interfaces'
import { useEffect, useState } from 'react'

import { useMyMonitoringAddOrUpdatePushRegistration } from '@apps-common/apis/my-monitoring/push/add-or-update-push-registration'
import { useMyMonitoringDeletePushRegistration } from '@apps-common/apis/my-monitoring/push/delete-push-registration'
import { AlertModal, IAlertModalProps } from '@apps-common/components/modals'
import { ModalDismiss } from '@apps-common/config/modal'
import { useAuth } from '@apps-common/contexts'
import { defaultModalAnimations } from '@apps-common/lib/modal-animations'
import { DelayKeys, useMyMonitoringNotificationsStore, useNotificationDelayStore } from '@apps-common/stores'
import { IPushRegistrationModel } from '@apps-common/types/portal/push-registration'
import { usePlatformSwitcher } from './usePlatformSwitcher'

const delayMs = 3 * 24 * 60 * 60 * 1000 // 3 days

export interface IMyMonitoringPushResult {
  registrationLoading: boolean
  error: boolean
  registration?: IPushRegistrationModel
}

var isProcessing = false

const setIsProcessing = (value: boolean) => {
  isProcessing = value
}

interface IProps {
  checkPermissions: () => Promise<'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'>
  requestPermissions: () => Promise<'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'>
  getToken: () => Promise<GetTokenResult>
}

export const useMyMonitoringPush = ({ checkPermissions, requestPermissions, getToken }: IProps): IMyMonitoringPushResult => {
  const [alertProps, setAlertProps] = useState<IAlertModalProps>()
  const { user } = useAuth()
  const { setDelay, isDelayed, clearAll } = useNotificationDelayStore()
  const { hydrated, notificationsEnabled, setNotificationsEnabled, registration, initRegistration } = useMyMonitoringNotificationsStore()
  const [processed, setProcessed] = useState<boolean>(false)

  const enabled = !!user

  const requestMessaging = usePlatformSwitcher({
    web: {
      title: 'Enable browser notifications',
      message: (
        <div>
          Enable browser notifications to stay updated on your sites.
          <br />
          <br />
          When prompted, click <strong>Allow</strong>. If you don’t see the prompt, check your browser’s address bar or site settings to
          enable notifications.
        </div>
      )
    },
    mobile: {
      title: 'Notifications',
      message: <div>Enable notifications to receive important updates and activity from your sites.</div>
    }
  })

  const disabledMessaging = usePlatformSwitcher({
    web: {
      title: 'Web Notifications',
      message:
        'Notifications are blocked. To receive updates about your sites, enable notifications in your browser settings (check the address bar or site permissions).'
    },
    mobile: {
      title: 'Notifications',
      message: 'Notifications are currently turned off. Enable them in your device settings to receive updates about your sites.'
    }
  })

  const {
    data,
    mutate: addRegistrationMutation,
    isPending: addIsPending,
    isError: addIsError
  } = useMyMonitoringAddOrUpdatePushRegistration()
  const {
    mutateAsync: removeRegistrationMutation,
    isPending: removeIsPending,
    isError: removeIsError
  } = useMyMonitoringDeletePushRegistration()

  useEffect(() => {
    if (addIsPending || removeIsPending) return

    if (enabled && notificationsEnabled != undefined && !!registration) {
      if (notificationsEnabled === true) {
        addRegistrationMutation({ model: registration })
      } else {
        removeRegistrationMutation({ pushId: registration.pushId })
        initRegistration(false, undefined)
      }
    }
  }, [enabled, notificationsEnabled, registration?.pushId, removeIsPending])

  const [present, dismiss] = useIonModal(AlertModal, {
    componentProps: alertProps,
    onDismiss: (data: string, role: string) => dismiss(data, role)
  })

  const processPermissionRequest = async () => {
    try {
      // if got to this stage clear all delays
      clearAll()

      const result = await requestPermissions()

      if (result !== 'granted') {
        setNotificationsEnabled(false)

        return
      }

      const tokenResult = await getToken()

      if (!tokenResult?.token) {
        setNotificationsEnabled(false)

        return
      }

      const platform = Capacitor.getPlatform()
      const isNative = Capacitor.isNativePlatform()
      let info: DeviceInfo | null = null
      let deviceId: DeviceId | null = null

      if (isNative) {
        // process native push parameters
        info = await Device.getInfo()
        deviceId = await Device.getId()
      }

      const regObj: IPushRegistrationModel = {
        id: 0,
        userId: 0,
        firebaseUserId: '',
        appName: '',
        platform,
        pushId: tokenResult.token,
        manufacturer: info?.manufacturer || 'unknown',
        model: info?.model || 'unknown',
        uuid: deviceId?.identifier || 'unknown',
        timestamp: new Date().toISOString()
      }

      initRegistration(true, regObj)
    } finally {
      setProcessed(true)
      setIsProcessing(false)
    }
  }

  const processPermissionsCheck = async () => {
    if (isProcessing) return

    setIsProcessing(true)

    const result = await checkPermissions()

    if (notificationsEnabled === false || result === 'prompt' || result === 'prompt-with-rationale') {
      // show popup explaining what the notifications are
      setAlertProps({
        status: 'info',
        title: requestMessaging.title,
        body: <div>{requestMessaging.message}</div>,
        options: [
          { text: 'Enable', status: 'success', role: ModalDismiss.Confirm },
          {
            text: 'Maybe later',
            status: 'neutral',
            role: ModalDismiss.Cancel
          }
        ]
      })
    } else if (result === 'denied') {
      // show prompt explains what will be missed with notifications not enabled
      setAlertProps({
        status: 'info',
        title: disabledMessaging.title,
        body: <div>{disabledMessaging.message}</div>,
        options: [
          { text: 'Ok', status: 'success', role: ModalDismiss.Confirm },
          {
            text: 'No thanks',
            status: 'warning',
            role: ModalDismiss.Cancel
          }
        ]
      })
    } else {
      // must be granted, proceed to register token
      console.log('Proceeding to register push token!!')
      processPermissionRequest()
    }

    setProcessed(true)
  }

  useEffect(() => {
    if (alertProps) {
      const delayed = isDelayed(DelayKeys.MyMonitoringNotificationPrompt)

      if (delayed) {
        setAlertProps(undefined)
        return
      }

      present({
        onDidDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
          if (ev.detail.role === ModalDismiss.Confirm) {
            console.log('Alert confirm selected')
            processPermissionRequest()
          } else if (ev.detail.role === ModalDismiss.Cancel) {
            console.log('Alert cancel selected')
            setNotificationsEnabled(false)
            setDelay(DelayKeys.MyMonitoringNotificationPrompt, delayMs)

            setIsProcessing(false)
          }
        },
        ...defaultModalAnimations
      })
    }
  }, [alertProps])

  useEffect(() => {
    if (!hydrated || !enabled || isProcessing || notificationsEnabled !== true || registration) {
      return
    }

    setIsProcessing(true)
    processPermissionRequest()
  }, [hydrated, enabled, notificationsEnabled, registration?.pushId])

  useEffect(() => {
    if (!hydrated || !enabled || isProcessing || (processed && !notificationsEnabled)) {
      return
    }

    if (!registration) {
      processPermissionsCheck()
    }
  }, [notificationsEnabled, registration])

  useEffect(() => {
    if (!hydrated || !enabled || notificationsEnabled === false) {
      return
    }

    processPermissionsCheck()
  }, [hydrated, enabled])

  const registrationLoading = addIsPending
  const error = addIsError

  return {
    registrationLoading,
    error,
    registration: data
  }
}
